import React, { useEffect, useState, useMemo } from 'react';
import { Card, CardContent, CardHeader } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Plus, Edit2, Trash2, Tag, Lock } from 'lucide-react';
import { toast } from 'sonner';

/**
 * iter76 — Custom Item Types panel.
 * Admin can add new item types and activate/deactivate any. The 5 system
 * defaults (RawMaterial / Component / Packaging / Consumable / Service)
 * are read-only — only their active flag can be toggled.
 */
const ItemTypesPanel = ({ api, canEdit }) => {
  const [types, setTypes] = useState([]);
  const [showInactive, setShowInactive] = useState(false);
  const [counts, setCounts] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', label: '', active: true });
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    try {
      const [tRes, iRes] = await Promise.all([
        api().get(`/item-types${showInactive ? '?include_inactive=true' : ''}`),
        api().get('/items'),
      ]);
      setTypes(tRes.data || []);
      const map = {};
      (iRes.data || []).forEach(it => {
        const k = (it.item_type || '').trim();
        if (k) map[k] = (map[k] || 0) + 1;
      });
      setCounts(map);
    } catch (e) { toast.error('Failed to load item types'); }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [showInactive]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', label: '', active: true });
    setDialogOpen(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({ name: t.name, label: t.label || '', active: !!t.active });
    setDialogOpen(true);
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api().put(`/item-types/${editing.id}`, form);
        toast.success('Item type updated');
      } else {
        await api().post('/item-types', form);
        toast.success('Item type created');
      }
      setDialogOpen(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const toggleActive = async (t) => {
    try {
      await api().put(`/item-types/${t.id}/activate?active=${!t.active}`);
      toast.success(`${t.name} ${!t.active ? 'activated' : 'deactivated'}`);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Toggle failed'); }
  };

  const remove = async (t) => {
    if (t.is_system) return; // safety
    const inUse = counts[t.name] || 0;
    if (inUse > 0) { toast.error(`${inUse} item(s) still use this type — deactivate it instead`); return; }
    if (!window.confirm(`Delete custom item type "${t.name}"?`)) return;
    try {
      await api().delete(`/item-types/${t.id}`);
      toast.success('Deleted');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const visible = useMemo(() => types.filter(t => showInactive || t.active), [types, showInactive]);

  return (
    <Card className="industrial-card" data-testid="item-types-panel">
      <CardHeader className="border-b border-slate-100 pb-3 flex-row items-center justify-between flex">
        <div>
          <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <Tag className="w-4 h-4 text-blue-600" /> Item Types (Product Types)
          </h2>
          <p className="text-xs text-slate-500 mt-1">Customize the dropdown shown in the Items form and the <code>item_type</code> column of the bulk import. System defaults are read-only.</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} className="accent-blue-500" data-testid="item-types-show-inactive" />
            <span>Show inactive</span>
          </label>
          {canEdit && (
            <Button size="sm" onClick={openCreate} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="item-types-add-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Item Type
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Display Label</TableHead>
              <TableHead className="text-center">Items</TableHead>
              <TableHead className="text-center">Active</TableHead>
              <TableHead className="text-center">Source</TableHead>
              {canEdit && <TableHead className="text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.map(t => (
              <TableRow key={t.id} data-testid={`item-type-row-${t.id}`} className={t.active ? '' : 'opacity-60'}>
                <TableCell className="font-medium mono">{t.name}</TableCell>
                <TableCell className="text-slate-600 text-sm">{t.label || '—'}</TableCell>
                <TableCell className="text-center text-slate-500">{counts[t.name] || 0}</TableCell>
                <TableCell className="text-center">
                  <Switch checked={!!t.active} onCheckedChange={() => canEdit && toggleActive(t)} disabled={!canEdit} data-testid={`item-type-active-${t.id}`} />
                </TableCell>
                <TableCell className="text-center">
                  {t.is_system ? (
                    <span className="inline-flex items-center gap-1 text-[10px] text-slate-500 uppercase tracking-wider"><Lock className="w-3 h-3" /> system</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[10px] text-blue-700 uppercase tracking-wider">custom</span>
                  )}
                </TableCell>
                {canEdit && (
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(t)} disabled={t.is_system} title={t.is_system ? 'System types are read-only' : 'Edit'} data-testid={`item-type-edit-${t.id}`}>
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(t)} disabled={t.is_system} className="text-red-600 disabled:text-slate-300" title={t.is_system ? 'System types cannot be deleted' : 'Delete'} data-testid={`item-type-delete-${t.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="item-type-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Item Type' : 'Add Item Type'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Name (used in CSV import) *</Label>
              <Input value={form.name} onChange={(e) => setForm(s => ({ ...s, name: e.target.value }))} placeholder="e.g., WeldedFrame" className="rounded-sm mono" disabled={editing?.is_system} data-testid="item-type-name-input" />
              <p className="text-[10px] text-slate-500 mt-1">No spaces recommended — this exact value goes in the <code>item_type</code> CSV column.</p>
            </div>
            <div>
              <Label className="text-xs">Display Label</Label>
              <Input value={form.label} onChange={(e) => setForm(s => ({ ...s, label: e.target.value }))} placeholder="e.g., Welded Frame" className="rounded-sm" data-testid="item-type-label-input" />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">Active</Label>
              <Switch checked={!!form.active} onCheckedChange={(v) => setForm(s => ({ ...s, active: v }))} data-testid="item-type-active-switch" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-slate-900" data-testid="item-type-submit-btn">{saving ? 'Saving…' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default ItemTypesPanel;
